import React, { Fragment, useMemo, useCallback } from "react";
import { Menu, Transition } from "@headlessui/react";
import { ChevronDownIcon, CheckIcon } from "@heroicons/react/24/solid";
import classNames from "classnames";
import PropTypes from "prop-types";
import { SORT_OPTIONS } from "@/utils/applicationConstants";

/**
 * Enhanced SortOption component with uniform sizing and styling
 *
 * @param {Object} props - Component props
 * @param {Object} props.query - Current query object containing sortBy
 * @param {Function} props.setQuery - Function to update query state
 * @param {Array} [props.entityOpts=SORT_OPTIONS] - Array of sort options
 * @param {Array} [props.exclude=[]] - Array of option IDs to exclude
 * @param {string} [props.size="md"] - Size variant (sm, md, lg)
 * @param {string} [props.variant="default"] - Style variant (default, outlined, minimal)
 * @param {string} [props.className] - Additional CSS classes
 * @param {boolean} [props.disabled=false] - Whether the component is disabled
 * @param {boolean} [props.showLabel=true] - Whether to show "Sort by" label
 * @param {string} [props.placeholder="Sort by"] - Placeholder text when no option selected
 */
export const EnhancedSortOption = React.memo(
  ({
    query,
    setQuery,
    entityOpts = SORT_OPTIONS,
    exclude = [],
    size = "md",
    variant = "default",
    className = "",
    disabled = false,
    placeholder = "Sort by",
  }) => {
    // Filter options based on exclude list
    const options = useMemo(() => entityOpts.filter(value => !exclude.includes(value.id)), [entityOpts, exclude]);

    // Find the currently selected option to display its name
    const selectedOption = useMemo(
      () => options.find(option => option.id === query.sortBy) || null,
      [options, query.sortBy],
    );

    // Memoized change handler for performance
    const changeSortOption = useCallback(
      id => {
        if (disabled || id === query.sortBy) return;

        setQuery(oldQuery => ({
          ...oldQuery,
          sortBy: id,
        }));
      },
      [setQuery, disabled, query.sortBy],
    );

    // Size variants
    const sizeClasses = {
      sm: {
        button: "h-8 px-3 text-sm",
        menuItems: "py-1 px-3 text-sm",
        icon: "w-4 h-4",
      },
      md: {
        button: "h-10 px-4 text-sm",
        menuItems: "py-2 px-4 text-sm",
        icon: "w-4 h-4",
      },
      lg: {
        button: "h-12 px-5 text-base",
        menuItems: "py-3 px-5 text-base",
        icon: "w-5 h-5",
      },
    };

    // Variant styles
    const variantClasses = {
      default: {
        button:
          "bg-white dark:bg-slate-800 border border-gray-300 dark:border-slate-600 hover:bg-gray-50 dark:hover:bg-slate-700",
        menu: "bg-white dark:bg-slate-800 border border-gray-200 dark:border-slate-600",
      },
      outlined: {
        button:
          "bg-white dark:bg-slate-800 border-2 border-gray-300 dark:border-slate-600 hover:border-indigo-500 dark:hover:border-indigo-400",
        menu: "bg-white dark:bg-slate-800 border-2 border-gray-200 dark:border-slate-600",
      },
      minimal: {
        button:
          "bg-transparent border-0 border-b-2 border-gray-200 dark:border-slate-600 hover:border-indigo-500 rounded-none",
        menu: "bg-white dark:bg-slate-800 border border-gray-200 dark:border-slate-600",
      },
    };

    const currentSize = sizeClasses[size];
    const currentVariant = variantClasses[variant];

    return (
      <div className={classNames("relative", className)}>
        <Menu as="div" className="relative inline-block w-full text-left">
          <Menu.Button
            disabled={disabled}
            className={classNames(
              "inline-flex w-full justify-between items-center rounded-md shadow-sm",
              "font-medium text-gray-700 dark:text-slate-200",
              "focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:ring-offset-2",
              "transition-colors duration-200",
              currentSize.button,
              currentVariant.button,
              disabled && "opacity-50 cursor-not-allowed",
            )}
          >
            <span className="truncate">{selectedOption ? selectedOption.name : placeholder}</span>
            <ChevronDownIcon className={classNames("ml-2 flex-shrink-0", currentSize.icon)} aria-hidden="true" />
          </Menu.Button>

          <Transition
            as={Fragment}
            enter="transition ease-out duration-100"
            enterFrom="transform opacity-0 scale-95"
            enterTo="transform opacity-100 scale-100"
            leave="transition ease-in duration-75"
            leaveFrom="transform opacity-100 scale-100"
            leaveTo="transform opacity-0 scale-95"
          >
            <Menu.Items
              className={classNames(
                "absolute right-0 z-10 mt-2 origin-top-right rounded-md shadow-lg",
                "ring-1 ring-black ring-opacity-5 focus:outline-none",
                "max-h-60 overflow-auto",
                currentVariant.menu,
              )}
            >
              <div className="py-1">
                {options.map(option => (
                  <Menu.Item key={option.id}>
                    {({ active }) => (
                      <button
                        type="button"
                        onClick={() => changeSortOption(option.id)}
                        className={classNames(
                          "flex w-full items-center justify-between text-left",
                          "text-gray-700 dark:text-slate-200",
                          currentSize.menuItems,
                          active ? "bg-gray-100 dark:bg-slate-700" : "",
                          selectedOption?.id === option.id ? "font-semibold" : "font-normal",
                        )}
                      >
                        <span className="whitespace-nowrap">{option.name}</span>
                        {selectedOption?.id === option.id && (
                          <CheckIcon className={classNames("flex-shrink-0 text-indigo-600", currentSize.icon)} />
                        )}
                      </button>
                    )}
                  </Menu.Item>
                ))}
              </div>
            </Menu.Items>
          </Transition>
        </Menu>
      </div>
    );
  },
);

EnhancedSortOption.displayName = "EnhancedSortOption";

EnhancedSortOption.propTypes = {
  query: PropTypes.object.isRequired,
  setQuery: PropTypes.func.isRequired,
  entityOpts: PropTypes.arrayOf(
    PropTypes.shape({
      id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired,
      name: PropTypes.string.isRequired,
    }),
  ),
  exclude: PropTypes.arrayOf(PropTypes.oneOfType([PropTypes.string, PropTypes.number])),
  size: PropTypes.oneOf(["sm", "md", "lg"]),
  variant: PropTypes.oneOf(["default", "outlined", "minimal"]),
  className: PropTypes.string,
  disabled: PropTypes.bool,
  placeholder: PropTypes.string,
};

export default EnhancedSortOption;
